/**
 * Identity cache by id for arrays emitted by PowerSync's LIVE queries
 * (recipe CC-23, packaged in meal `6b53c9a`; pilot CC-26 in
 * field-check).
 *
 * Live queries emit NEW arrays and objects on each tick even if
 * content does not change. A `<For>` (non-keyed), a `<Show keyed>` or
 * `renderItem` of Kobalte Listbox reconcile by REFERENTIAL identity,
 * so each tick remounts nodes → clicks land on stale nodes
 * (CC-26: query-backed Select "drops the click") and local inputs
 * reset mid-sync.
 *
 * `reconcileById(prev, next)` returns an array that REUSES the previous object
 * when its content did not change (shallow comparison by default), and the
 * PREVIOUS array itself if nothing changed — so neither `<For>` nor `keyed` nor
 * Kobalte's collection-memo remounts anything on a tick with no real changes.
 *
 * Use with Solid:
 * ```ts
 * const stableRows = createMemo<Row[]>((prev) =>
 *   reconcileById(prev ?? [], query.data ?? [], (r) => r.id),
 * );
 * ```
 */

function shallowEqual(a: object, b: object): boolean {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) {
    return false;
  }
  for (const key of aKeys) {
    if (!Object.is(Reflect.get(a, key), Reflect.get(b, key))) {
      return false;
    }
  }
  return true;
}

export function reconcileById<T extends object>(
  prev: T[],
  next: readonly T[],
  getId: (item: T) => string | number | null | undefined,
  isEqual: (a: T, b: T) => boolean = shallowEqual,
): T[] {
  const prevById = new Map<string | number, T>();
  for (const item of prev) {
    const id = getId(item);
    if (id !== null && id !== undefined) {
      prevById.set(id, item);
    }
  }

  let reusedAll = prev.length === next.length;
  const result = next.map((item, index) => {
    const id = getId(item);
    const previous = id === null || id === undefined ? undefined : prevById.get(id);
    if (previous && isEqual(previous, item)) {
      if (reusedAll && prev[index] !== previous) {
        reusedAll = false; // same content but reordered
      }
      return previous;
    }
    reusedAll = false;
    return item;
  });

  // Nothing changed (same objects, same order): preserve the ARRAY's identity
  // so downstream memos do not re-emit either.
  return reusedAll ? prev : result;
}
